// Cliente da API (mesma origem via /api; cookie de sessão httpOnly).
import { config } from "./config";
import { lang, t } from "./i18n";

export interface User {
  id: string;
  username: string;
  nickname: string;
  email: string;
  emailVerified: boolean;
  accountLevel: number;
  crystals: number;
  pokedollars: number;
  avatarSpecies: number | null;
  /** player | moderator | admin */
  role?: string;
  chatMutedUntil?: string | null;
  title?: string | null;
  frame?: string | null;
  tutorialDone?: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public fields: Record<string, string> = {},
    public extra: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, {
      method,
      credentials: "include",
      headers: body === undefined ? {} : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "offline");
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(res.status, String(data.error ?? "internal"), (data.fields as Record<string, string>) ?? {}, data);
  }
  return data as T;
}

export const api = {
  me: () => request<User>("GET", "/me"),
  register: (b: { username: string; email: string; nickname: string; password: string }) => request<User>("POST", "/auth/register", b),
  login: (b: { login: string; password: string }) => request<User>("POST", "/auth/login", b),
  logout: () => request<void>("POST", "/auth/logout"),
  verifyEmail: (token: string) => request<{ ok: true }>("POST", "/auth/verify-email", { token }),
  resendVerification: () => request<{ ok: true }>("POST", "/auth/resend-verification"),
  forgotPassword: (email: string) => request<{ ok: true }>("POST", "/auth/forgot-password", { email }),
  resetPassword: (token: string, password: string) => request<{ ok: true }>("POST", "/auth/reset-password", { token, password }),
  updateNickname: (nickname: string) => request<User>("PATCH", "/me", { nickname }),
  gameToken: () => request<{ token: string; expiresIn: number }>("POST", "/game-token"),
  cards: () => request<CardsResponse>("GET", "/cards"),
  saveDeck: (deck: { cardId: string; tier: CardTier }[]) => request<{ ok: true }>("PUT", "/cards/deck", { deck }),
  ranking: () => request<RankingResponse>("GET", "/ranking"),
  currentMatch: () => request<{ match: { matchId: string; roomId: string } | null }>("GET", "/match/current"),
  history: () => request<{ matches: { id: string; mode: string; ended_at: string; max_players: number; placement: number; points_delta: number | null }[] }>("GET", "/history"),
  cosmetics: () => request<{ cosmetics: Cosmetic[]; pokedollars: number }>("GET", "/cosmetics"),
  buyCosmetic: (id: string) => request<{ ok: true; pokedollars: number }>("POST", `/cosmetics/${encodeURIComponent(id)}/buy`),
  buyCard: (cardId: string, tier: CardTier) => request<{ ok: true; pokedollars: number }>("POST", "/cards/buy", { cardId, tier }),
  items: () => request<{ pokedollars: number; items: { id: string; kind: "component" | "item" | "special"; price: number; owned: boolean }[] }>("GET", "/items"),
  buyItem: (id: string) => request<{ ok: true; pokedollars: number }>("POST", `/items/${encodeURIComponent(id)}/buy`),
  equip: (slot: string, cosmeticId: string | null) => request<{ ok: true }>("PUT", "/cosmetics/loadout", { slot, cosmeticId }),
  friends: () => request<FriendsResponse>("GET", "/friends"),
  friendRequest: (username: string) => request<{ ok: true; status: "pending" | "accepted" }>("POST", "/friends/request", { username }),
  acceptFriend: (id: string) => request<{ ok: true }>("POST", `/friends/${encodeURIComponent(id)}/accept`),
  removeFriend: (id: string) => request<{ ok: true }>("DELETE", `/friends/${encodeURIComponent(id)}`),
  globalChat: () => request<{ messages: ChatMessage[] }>("GET", "/chat/global"),
  postGlobal: (text: string) => request<{ ok: true; message: ChatMessage }>("POST", "/chat/global", { text }),
  dm: (id: string) => request<{ messages: ChatMessage[] }>("GET", `/chat/dm/${encodeURIComponent(id)}`),
  postDm: (id: string, text: string) => request<{ ok: true; message: ChatMessage }>("POST", `/chat/dm/${encodeURIComponent(id)}`, { text }),
  missions: () => request<MissionsResponse>("GET", "/missions"),
  claimMission: (slot: number) => request<{ ok: true; reward: number; pokedollars: number; completed: number }>("POST", `/missions/${slot}/claim`),
  leaderboard: (kind: LeaderboardKind) => request<LeaderboardResponse>("GET", `/leaderboard/${kind}`),
  report: (b: { targetId: string; reason: string; details?: string; matchId?: string; roomChat?: { nickname: string; text: string; at: number }[] }) =>
    request<{ ok: true; autoMuted?: boolean; duplicate?: boolean }>("POST", "/reports", b),
  blocks: () => request<{ blocked: PublicUser[] }>("GET", "/blocks"),
  block: (id: string) => request<{ ok: true }>("POST", `/blocks/${id}`),
  unblock: (id: string) => request<{ ok: true }>("DELETE", `/blocks/${id}`),
  modReports: (status: "open" | "closed") => request<{ reports: ModReport[] }>("GET", `/mod/reports${status === "closed" ? "?status=closed" : ""}`),
  modResolve: (id: number, action: "dismiss" | "mute" | "rename" | "ban", hours?: number) =>
    request<{ ok: true }>("POST", `/mod/reports/${id}/resolve`, { action, ...(hours ? { hours } : {}) }),
  achievements: () => request<AchievementsResponse>("GET", "/achievements"),
  achievementsUnseen: () => request<{ unseen: number; fresh: { id: string }[] }>("GET", "/achievements/unseen"),
  setTitle: (title: string | null) => request<{ ok: true }>("POST", "/me/cosmetic-title", { title }),
  setFrame: (frame: string | null) => request<{ ok: true }>("POST", "/me/frame", { frame }),
  skipTutorial: () => request<{ ok: true }>("POST", "/me/tutorial/skip"),
  creche: () => request<CrecheResponse>("GET", "/creche"),
  adopt: (unitId: string) => request<{ ok: true }>("POST", "/creche/adopt", { unitId }),
  release: () => request<{ ok: true }>("DELETE", "/creche"),
  petAction: (action: "feed" | "play" | "train") => request<{ ok: true; xp: number; unlocked: string | null }>("POST", `/creche/${action}`),
  config: () => request<{ features: { vipShop: boolean; globalChat: boolean; changelog: boolean } }>("GET", "/config"),
  avatars: () => request<{ species: number[] }>("GET", "/avatars"),
  setAvatar: (species: number | null) => request<{ ok: true; avatarSpecies: number | null }>("PUT", "/me/avatar", { species }),
  shop: () => request<ShopResponse>("GET", "/shop"),
  buyBoard: (id: string) => request<{ ok: true; crystals: number }>("POST", `/shop/boards/${encodeURIComponent(id)}/buy`),
  equipBoard: (boardId: string | null) => request<{ ok: true }>("PUT", "/shop/boards/equipped", { boardId }),
  checkout: (packageId: string) => request<{ donationId: string; url: string; mode: "dev" | "mercadopago" }>("POST", "/donations/checkout", { packageId }),
  donation: (id: string) => request<{ donation: Donation; crystals: number }>("GET", `/donations/${encodeURIComponent(id)}`),
  donations: () => request<{ donations: Donation[] }>("GET", "/donations"),
  devPay: (id: string, approve: boolean) => request<{ ok: true }>("POST", `/donations/${encodeURIComponent(id)}/dev-pay`, { approve }),
};

export interface ModReport {
  id: number; reason: string; details: string | null; match_id: string | null; status: string; action: string | null;
  created_at: string; resolved_at: string | null; reporter: string; target_id: string; target_nickname: string; target_username: string;
  chat_muted_until: string | null; banned_until: string | null; target_open: number; target_reporters: number;
  evidence: { nickname: string; username: string; chat: { channel: string; text: string; at: string }[]; roomChat: { nickname: string; text: string; at: number }[] };
}
export interface AchievementDto {
  id: string; category: string; goal: number; progress: number; secret: boolean; unlockedAt: string | null; isNew: boolean;
  reward: { pokedollars?: number; title?: string; frame?: string };
}
export interface AchievementsResponse {
  achievements: AchievementDto[];
  fresh: { id: string; pokedollars: number }[];
  equipped: { title: string | null; frame: string | null };
  titles: string[];
  frames: string[];
}
export type LeaderboardKind = "level" | "missions" | "streak";
export interface LeaderboardResponse {
  kind: LeaderboardKind;
  top: { position: number; nickname: string; avatar: number | null; value: number; extra: number; me: boolean }[];
  me: { position: number | null; value: number; extra: number } | null;
}
export interface MissionDto {
  kind: string; target: number; reward: number; trait?: string; count?: number; need?: number;
}
export interface MissionsResponse {
  day: string;
  resetsAt: number;
  missions: { slot: number; mission: MissionDto; progress: number; target: number; reward: number; claimed: boolean }[];
  streak: { streak: number; best: number };
  completed: number;
}
export interface PetState { unitId: string; level: number; xp: number; hunger: number; happiness: number; updatedAt: number; lastPlay: number; lastTrain: number }
export interface CrecheResponse {
  pet: PetState | null;
  unlocked: string[];
  lockedLines: string[];
  pokedollars: number;
  now: number;
  rules: { maxLevel: number; xpPerLevel: number; feedPrice: number; playCooldownMs: number; trainCooldownMs: number };
}
export interface PublicUser { id: string; username: string; nickname: string; avatar: number | null }
export interface FriendsResponse { friends: (PublicUser & { online: boolean })[]; incoming: PublicUser[]; outgoing: PublicUser[] }
export interface ChatMessage { id: number; text: string; at: string; from: { id: string; nickname: string; avatar: number | null } }
export interface ShopBoard { id: string; name: string; desc: string; price: number; animated: boolean; fx?: string; owned: boolean; equipped: boolean }
export interface ShopResponse {
  crystals: number;
  payments: "mercadopago" | "dev" | "off";
  packages: { id: string; name: string; crystals: number; bonus: number; priceCents: number }[];
  boards: ShopBoard[];
}
export interface Donation {
  id: string;
  package_id: string;
  crystals: number;
  amount_cents: number;
  status: "pending" | "in_process" | "approved" | "rejected" | "cancelled" | "refunded" | "charged_back";
  credited: boolean;
  created_at: string;
}

export type CardTier = "copper" | "silver" | "gold";
export interface CardsResponse {
  setId: string;
  pokedollars: number;
  rules: { slots: CardTier[]; categories: Record<string, { name: string; maxPerDeck: number }> };
  cards: { id: string; name: string; category: string; trait: string | null; tiers: { tier: CardTier; value: number | null; desc: string; price: number; owned: boolean }[] }[];
  deck: { tier: CardTier; cardId: string | null }[];
}
export interface RankingResponse {
  season: { id: number; name: string; endsAt: string } | null;
  top: { position: number; userId: string; nickname: string; tier: string; division: number; points: number; label: string; games: number; wins: number }[];
  me: { tier: string; division?: number; points?: number; label: string; games: number; position: number | null; placement_games_left?: number; peak_tier?: string } | null;
}
export interface Cosmetic {
  id: string;
  type: string;
  target_species: number | null;
  rarity: string;
  /** preço em Pokédollar (null = não está à venda) */
  price: number | null;
  season: string | null;
  owned: boolean;
  equipped: boolean;
}

/** Mensagem traduzida para os códigos de erro da API. */
export function errorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return t("common.error");
  switch (e.code) {
    case "offline": return t("err.offline");
    case "invalid-credentials": return t("err.invalidCredentials");
    case "too-many-attempts": return t("err.tooManyAttempts");
    case "rate-limited": return t("err.rateLimited");
    case "username-taken": return t("err.usernameTaken");
    case "email-taken": return t("err.emailTaken");
    case "invalid-token": return t("err.invalidToken");
    case "banned": return t("err.banned");
    case "nickname-cooldown": return t("err.nicknameCooldown", { days: Number(e.extra.days ?? 7) });
    case "already-verified": return t("err.alreadyVerified");
    case "unauthenticated": return t("err.unauthenticated");
    case "validation": return t("err.validation");
    case "invalid-deck": return lang() === "pt-BR" ? ((e.extra.errors as string[]) ?? []).join(" · ") || t("err.invalidDeck") : t("err.invalidDeck");
    case "not-owned": return t("err.notOwned");
    case "already-owned": return t("err.alreadyOwned");
    case "not-enough-crystals": return t("shop.notEnough");
    case "not-enough-pokedollars": return t("pdx.notEnough");
    case "user-not-found": return t("social.err.notFound");
    case "self-friend": return t("social.err.self");
    case "already-friends": return t("social.err.already");
    case "not-friends": return t("social.err.notFriends");
    case "payments-off": return t("shop.paymentsOff");
    case "payment-provider": return t("err.paymentProvider");
    default: return t("common.error");
  }
}
